import React from 'react';
import { translate } from 'translations/i18n';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useModal } from 'components/shared/hooks';
import { ButtonIconCondensed, Menu, MenuItem } from '@companycam/slab-web';
import {
  LD_WORKFLOWS_PAGE_TEMPLATES,
  LD_EXPORT_PAGE_PDF_FLAG,
} from 'constants/featureFlags';

import { ADMIN, MANAGER, hasRoleAccess } from 'helpers/roles';
import { useCurrentUser } from 'javascripts/hooks';

// Menu
import { useSaveAsPageTemplate, useDeleteProjectPage } from 'data/v3';
import toast from 'helpers/toast';
import steven from 'helpers/steven';
import { NewBadge } from 'components/shared/Badge';
import styled from 'styled-components';

const SaveAsTemplateNewContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

type ProjectPageMenuActionsProps = {
  projectDbId: number;
  pageDbId: number;
  onDeleteSuccess?: () => void;
  onShowTranscript?: () => void;
  isShowingTranscript?: boolean;
  hasTranscript?: boolean;
  onExportPdf?: () => void;
  dataTestIdPrefix?: string;
};

const ProjectPageMenuActions = ({
  projectDbId,
  pageDbId,
  onDeleteSuccess,
  onShowTranscript,
  isShowingTranscript,
  hasTranscript,
  onExportPdf, // TODO: Bring this into component once project page is moved out of modal
  dataTestIdPrefix,
}: ProjectPageMenuActionsProps) => {
  const flags = useFlags();
  const isSavePageAsTemplateEnabled = flags[LD_WORKFLOWS_PAGE_TEMPLATES];
  const isPdfExportEnabled = flags[LD_EXPORT_PAGE_PDF_FLAG];

  const user = useCurrentUser();
  const canSaveAsTemplate = hasRoleAccess(user, [ADMIN, MANAGER]);

  const saveAsPageTemplate = useSaveAsPageTemplate({
    pageDbId,
    onSuccess: () => {
      toast.success(
        translate('projectPage.toast.saveAsTemplateSuccess'),
        translate('projectPage.toast.saveAsTemplateAction'),
        () => {
          window.Turbo.visit('/templates/pages');
        },
        6,
      );
    },
    onError: () => {
      toast.error(translate('template.shared.templateCreatedError'));
    },
  });

  const { mutate: deleteProjectPage } = useDeleteProjectPage({
    projectDbId,
    onSuccess: () => {
      steven.entityCrud({
        action: 'deleted',
        entity_type: 'page',
        entity_id: `${pageDbId}`,
      });

      onDeleteSuccess();
    },
  });

  const { showModal, hideModal, Modal } = useModal();

  const handleDelete = () => {
    showModal(Modal.CONFIRMATION, {
      onRequestClose: hideModal,
      confirmAction: () => {
        hideModal();
        deleteProjectPage({
          pageDbId,
        });
      },
      useSecondaryConfirmation: false,
      confirmLabel: translate('common.confirmDelete'),
      cancelLabel: translate('common.cancelDelete'),
      message: translate('projectPage.deleteModal.body'),
      title: translate('projectPage.deleteModal.title'),
    });
  };

  const handleSaveAsTemplateClick = () => {
    showModal(Modal.SAVE_PAGE_AS_TEMPLATE_MODAL, {
      onSaveAsTemplate: (title: string, description: string) => {
        hideModal();
        saveAsPageTemplate.mutate({
          title,
          description,
        });
      },
    });
  };

  return (
    <Menu
      style={{
        maxWidth: '280px',
        minWidth: '224px',
      }}
      triggerButton={
        <ButtonIconCondensed
          accessibilityLabel={translate('common.menu')}
          iconName="dots-horizontal"
          data-testid={`${dataTestIdPrefix}__menu-trigger`}
        />
      }
    >
      {hasTranscript ? (
        <MenuItem iconName="file-outline" onClick={onShowTranscript}>
          {isShowingTranscript
            ? translate('projectPage.closeTranscript')
            : translate('projectPage.viewTranscript')}
        </MenuItem>
      ) : null}

      {isPdfExportEnabled && onExportPdf ? (
        <MenuItem
          iconName="file-pdf-box"
          onClick={onExportPdf}
          data-testid={`${dataTestIdPrefix}__export-pdf`}
        >
          {translate('common.exportPdf')}
        </MenuItem>
      ) : null}

      {isSavePageAsTemplateEnabled && canSaveAsTemplate ? (
        <MenuItem
          iconName="file-document-plus-outline"
          onClick={handleSaveAsTemplateClick}
          data-testid={`${dataTestIdPrefix}__save-as-template`}
        >
          <SaveAsTemplateNewContainer>
            {translate('projectPage.saveAsTemplate')}
            <NewBadge />
          </SaveAsTemplateNewContainer>
        </MenuItem>
      ) : null}

      <MenuItem
        iconName="trash-can-outline"
        onClick={handleDelete}
        data-testid={`${dataTestIdPrefix}__menu-delete`}
      >
        {translate('common.delete')}
      </MenuItem>
    </Menu>
  );
};

export default ProjectPageMenuActions;
